import { useRef, useEffect, useCallback } from 'react';
import { CheckoutEventNames, PaddleEventData } from '@paddle/paddle-js';
import { EventsEnum } from '@hubxtech/hubx-client-analytics-package/lib/src/enum/pdfGenius/eventsEnum';
import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { showSide } from '@/store/slices/sideSlice';
import { checkoutService } from '@/features/subscription/services/checkoutService';
import useAuth from '@/hooks/useAuth';
import { usePayment } from '@/features/subscription/hooks/usePayment';
import { usePlan } from './usePlan';
import { useWindowData } from './useWindowData';
import { sendGAEvent } from '@next/third-parties/google';
import {
  COMMON_EVENTS,
  customEventPush,
  getUserData,
  logHubxEvent,
} from '@/app/providers/analytics';
import { analytics } from '@/lib/firebaseConfig';
import { logEvent } from 'firebase/analytics';
import { RootState } from '@/store';
import { emit } from '@/utils/emitter';
import { store } from '@/store';
import { resolveAnalyticsPaywallType } from '@/features/subscription/utils/analyticsPaywall';
import { formatAnalyticsSource } from '@/helpers/common';
import { PlanStatusEnum } from '@/features/subscription/enum/planStatus';
import {
  paymentService,
  PaymentTransaction,
} from '@/features/subscription/services/paymentService';
import { checkoutVisibilityStore } from '@/features/subscription/utils/checkout/checkoutVisibilityStore';
import { transactionPromiseStore } from '@/features/subscription/utils/checkout/transactionPromiseStore';
import { checkoutRevealStore } from '@/features/subscription/utils/checkout/checkoutRevealStore';
import { checkoutPricingStore } from '@/features/subscription/utils/checkout/checkoutPricingStore';

const CHECKOUT_VERIFICATION_STORAGE_KEY = 'checkoutVerification';
const CHECKOUT_VERIFICATION_TIMEOUT_MS = 30000;
const CHECKOUT_VERIFICATION_RETRY_MS = 3000;

type PendingCheckoutVerification = {
  startedAt: number;
  paddleEventData: any;
  paywallType?: string;
  toolType?: string;
  transactionId?: string;
  subscriptionId?: string;
};

const extractPricingSnapshot = (paddleEvent: PaddleEventData) => {
  const data = (paddleEvent as any)?.data;
  const priceId = data?.items?.[0]?.price_id;
  const currencyCode = data?.currency_code;
  const dueTodayAmount = data?.totals?.total;
  const recurringAmount = data?.recurring_totals?.total ?? data?.totals?.total;

  if (
    !priceId ||
    !currencyCode ||
    dueTodayAmount == null ||
    recurringAmount == null
  ) {
    return null;
  }

  return {
    priceId,
    currencyCode,
    dueTodayAmount: Number(dueTodayAmount),
    recurringAmount: Number(recurringAmount),
  };
};

const usePaddleEvents = () => {
  const { user, refreshModelUser } = useAuth();
  const { setPlan, clearPlan, refreshData } = usePlan();
  const dispatch = useAppDispatch();
  const { updatePaymentStatus } = usePayment();
  const {
    data: pendingCheckoutVerification,
    setData: setPendingCheckoutVerification,
  } = useWindowData<PendingCheckoutVerification>(
    CHECKOUT_VERIFICATION_STORAGE_KEY
  );
  const typeForUserAccessLevel = useAppSelector(
    (state: RootState) => state.userAccess.typeForUserAccessLevel
  );
  const freeTypeForUserAccessLevel = useAppSelector(
    (state: RootState) => state.userAccess.freeTypeForUserAccessLevel
  );
  const userRef = useRef(user);
  const typeForUserAccessLevelRef = useRef(typeForUserAccessLevel);
  const freeTypeForUserAccessLevelRef = useRef(freeTypeForUserAccessLevel);
  const paymentInitiatedTimeRef = useRef<number | null>(null);
  const checkoutPurchaseClickLoggedRef = useRef(false);
  const verificationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const verificationInFlightRef = useRef(false);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    typeForUserAccessLevelRef.current = typeForUserAccessLevel;
  }, [typeForUserAccessLevel]);

  useEffect(() => {
    freeTypeForUserAccessLevelRef.current = freeTypeForUserAccessLevel;
  }, [freeTypeForUserAccessLevel]);

  const clearVerificationTimer = useCallback(() => {
    if (verificationTimerRef.current) {
      clearTimeout(verificationTimerRef.current);
      verificationTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearVerificationTimer();
    };
  }, [clearVerificationTimer]);

  const resolveCheckoutToolType = (paddleEvent: PaddleEventData) => {
    const customData = (paddleEvent as any)?.data?.custom_data;
    const eventToolType = customData?.tool_type || customData?.toolType;

    if (eventToolType) {
      return eventToolType;
    }

    const state = store.getState();
    const activeModal = state.modal.activeModal;
    const modalToolType = activeModal
      ? state.modal.modals?.[activeModal]?.modalData?.toolType
      : undefined;

    if (modalToolType) {
      return modalToolType;
    }

    const sideToolType = state.side.sideData?.toolType;
    if (sideToolType) {
      return sideToolType;
    }

    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      const searchTool = url.searchParams.get('tool');
      if (searchTool) {
        return searchTool;
      }

      const pathSegments = url.pathname.split('/').filter(Boolean);
      return pathSegments[pathSegments.length - 1];
    }

    return undefined;
  };

  const resolveCheckoutPaywallType = (paddleEvent: PaddleEventData) => {
    const customData = (paddleEvent as any)?.data?.custom_data;
    const eventPaywallType =
      customData?.paywall_type || customData?.paywallType;

    if (eventPaywallType) {
      return eventPaywallType;
    }

    const state = store.getState();
    const activeModal = state.modal.activeModal;
    const modalPaywallType = activeModal
      ? state.modal.modals?.[activeModal]?.modalData?.paywallType
      : undefined;

    if (modalPaywallType) {
      return modalPaywallType;
    }

    const sidePaywallType = state.side.sideData?.paywallType;
    if (sidePaywallType) {
      return sidePaywallType;
    }

    return resolveAnalyticsPaywallType({
      paywallType:
        state.userAccess.typeForUserAccessLevel ??
        typeForUserAccessLevelRef.current,
      toolType: resolveCheckoutToolType(paddleEvent),
    });
  };

  const clearPendingVerification = useCallback(() => {
    clearVerificationTimer();
    verificationInFlightRef.current = false;
    setPendingCheckoutVerification(null);
  }, [clearVerificationTimer, setPendingCheckoutVerification]);

  const resolveCheckoutFailureSource = useCallback(
    (paddleEvent: PaddleEventData, error?: any) =>
      paddleEvent?.error?.detail ||
      error?.data?.message ||
      error?.message ||
      'checkout_failed',
    []
  );

  const handleConfirmedCheckoutFailure = useCallback(
    (paddleEvent: PaddleEventData, error?: any) => {
      clearPendingVerification();
      updatePaymentStatus('failed');
      sendGAEvent('event', 'checkout_failed', {
        error: resolveCheckoutFailureSource(paddleEvent, error),
        timestamp: new Date().toISOString(),
      });
      const paywallType = resolveCheckoutPaywallType(paddleEvent);
      logHubxEvent({
        eventName: COMMON_EVENTS.PAYMENT_FAILED,
        event_params: {
          info: 'fail',
          paywallType,
          source: resolveCheckoutFailureSource(paddleEvent, error),
        },
      });
      const freePaywallType = freeTypeForUserAccessLevelRef.current;

      if (freePaywallType && freePaywallType !== paywallType) {
        logHubxEvent({
          eventName: COMMON_EVENTS.PAYMENT_FAILED,
          event_params: {
            info: 'fail',
            paywallType: freePaywallType,
            source: resolveCheckoutFailureSource(paddleEvent, error),
          },
        });
      }

      dispatch(
        showSide({
          sideKey: 'paymentFailed',
        })
      );
    },
    [
      clearPendingVerification,
      dispatch,
      resolveCheckoutFailureSource,
      updatePaymentStatus,
    ]
  );

  const finalizeCheckoutSuccess = useCallback(
    async (
      paddleEvent: any,
      completion?: {
        planData?: any;
        subscriptionData?: any;
      }
    ) => {
      clearPendingVerification();

      if (completion?.planData) {
        clearPlan();
        setPlan(completion.planData, completion.subscriptionData);
      }

      const refreshedUserResponse = await refreshModelUser();
      if (refreshedUserResponse?.user) {
        userRef.current = refreshedUserResponse.user;
      }

      if (!completion?.planData || !completion?.subscriptionData) {
        await refreshData({ withUserRefresh: true });
      }

      const state = store.getState();
      const resolvedPlanData = completion?.planData ?? state.plan.plan;
      const resolvedSubscriptionId =
        completion?.subscriptionData?.id ??
        state.plan.planDetails?.id ??
        state.plan.plan?.subscriptionId;

      if (userRef.current && paddleEvent.data) {
        const analyticsUserData = getUserData(userRef.current);
        const paywallType = resolveCheckoutPaywallType(paddleEvent);
        const freePaywallType = freeTypeForUserAccessLevelRef.current;
        customEventPush({
          data: {
            event_name: 'purchase_success',
            externalId: analyticsUserData.external_id,
            email: analyticsUserData.email,
            first_name: analyticsUserData.first_name,
            conversionValue: paddleEvent.data.totals.total,
            last_name: analyticsUserData.last_name,
            value: String(paddleEvent.data.totals.total),
            currency: paddleEvent.data.currency_code,
            transactionId: paddleEvent.data.transaction_id,
            item_id: paddleEvent.data.items?.[0]?.price_id,
            item_name: 'Premium',
            customer_user_id: paddleEvent.data.customer?.id,
            country_code: paddleEvent.data.customer?.address?.country_code,
            subscriptionId: resolvedSubscriptionId,
          },
          ignoreDuplicateCheck: true,
        });

        const amount = paddleEvent.data.totals.total;
        const currency = paddleEvent.data.currency_code;
        const completedItem = paddleEvent.data.items?.[0];
        const product_id =
          completedItem?.product?.id ||
          completedItem?.product_id ||
          completedItem?.price?.product_id ||
          completedItem?.price_id;

        logHubxEvent({
          eventName: COMMON_EVENTS.PREMIUM_SUCCESSFULLY_GRANTED,
          event_params: {
            paywallType,
            currency,
            amount,
            product_id,
          },
        });

        logHubxEvent({
          eventName: COMMON_EVENTS.PURCHASE_SUCCESS,
          event_params: {
            currency,
            amount,
            product_id,
            paywallType,
          },
        });

        logHubxEvent({
          eventName: COMMON_EVENTS.PADDLE_SUCCESS,
          event_params: {
            paywallType,
          },
        });

        if (freePaywallType && freePaywallType !== paywallType) {
          setTimeout(() => {
            logHubxEvent({
              eventName: COMMON_EVENTS.PREMIUM_SUCCESSFULLY_GRANTED,
              event_params: {
                paywallType: freePaywallType,
                currency,
                amount,
                product_id,
              },
            });

            logHubxEvent({
              eventName: COMMON_EVENTS.PURCHASE_SUCCESS,
              event_params: {
                currency,
                amount,
                product_id,
                paywallType: freePaywallType,
              },
            });

            logHubxEvent({
              eventName: COMMON_EVENTS.PADDLE_SUCCESS,
              event_params: {
                paywallType: freePaywallType,
              },
            });
          }, 1000);
        }

        if (analytics) {
          logEvent(analytics, 'purchase_success', {
            value: paddleEvent.data.totals.total,
            currency: paddleEvent.data.currency_code,
            transaction_id: paddleEvent.data.transaction_id,
            item_id: paddleEvent.data.items?.[0]?.price_id,
            item_name: 'Premium',
            coupon: paddleEvent.data.discount?.id,
            subscription_id: resolvedSubscriptionId,
            plan: resolvedPlanData?.uid,
            tax: paddleEvent.data.totals?.tax ?? 0.0,
            shipping: 0.0,
          });
        }

        if (typeof window !== 'undefined' && (window as any).dataLayer) {
          (window as any).dataLayer.push({
            event: 'purchase_success_v2',
            internal_use: false,
            user_data: {
              ...analyticsUserData,
              country: paddleEvent.data.customer?.address?.country_code,
            },
            value: paddleEvent.data.totals.total,
            currency: paddleEvent.data.currency_code,
            tax: paddleEvent.data.totals?.tax ?? 0.0,
            shipping: 0.0,
            transaction_id: paddleEvent.data.transaction_id,
            plan: resolvedPlanData?.uid,
            coupon: paddleEvent.data.discount?.id,
            subscription_id: resolvedSubscriptionId,
          });
        }
      }

      if (resolvedPlanData?.uid && resolvedSubscriptionId) {
        sendGAEvent('event', 'checkout_completed', {
          planId: resolvedPlanData.uid,
          subscriptionId: resolvedSubscriptionId,
          timestamp: new Date().toISOString(),
        });
      }

      dispatch(
        showSide({
          sideKey: null,
        })
      );
      updatePaymentStatus('completed');
      emit('download:step-completed');
      emit('process:step-completed');

      if (completion?.planData && completion?.subscriptionData) {
        await refreshData({ withUserRefresh: true });
      }
    },
    [
      clearPendingVerification,
      clearPlan,
      dispatch,
      refreshData,
      refreshModelUser,
      setPlan,
      updatePaymentStatus,
    ]
  );

  const isVerificationCandidate = useCallback((error: any) => {
    const status = error?.status;

    if (typeof status !== 'number') {
      return true;
    }

    return status >= 500;
  }, []);

  const extractSubscription = useCallback((subscriptionResponse: any) => {
    return subscriptionResponse?.data ?? subscriptionResponse ?? null;
  }, []);

  const hasConfirmedTransaction = useCallback(
    (
      pending: PendingCheckoutVerification,
      transactions: PaymentTransaction[]
    ) => {
      if (!pending.transactionId) {
        return false;
      }

      return transactions.some(
        (transaction) =>
          transaction.id === pending.transactionId &&
          ['billed', 'paid', 'completed'].includes(transaction.status)
      );
    },
    []
  );

  const hasConfirmedSubscription = useCallback(
    (pending: PendingCheckoutVerification, subscriptionResponse: any) => {
      const subscription = extractSubscription(subscriptionResponse);

      return (
        !!subscription &&
        !!pending.subscriptionId &&
        (subscription.status === PlanStatusEnum.ACTIVE ||
          subscription.status === PlanStatusEnum.TRIALING) &&
        subscription.id === pending.subscriptionId
      );
    },
    [extractSubscription]
  );

  const verifyPendingCheckout = useCallback(
    async (pending: PendingCheckoutVerification) => {
      if (verificationInFlightRef.current) {
        return;
      }

      verificationInFlightRef.current = true;
      clearVerificationTimer();

      let shouldRetry = false;

      try {
        const [subscriptionResult, transactionsResult] =
          await Promise.allSettled([
            paymentService.getSubscription(),
            paymentService.getTransactions({
              statusList: ['billed', 'paid', 'completed'],
            }),
          ]);

        const subscriptionResponse =
          subscriptionResult.status === 'fulfilled'
            ? subscriptionResult.value.data
            : null;
        const transactions =
          transactionsResult.status === 'fulfilled'
            ? transactionsResult.value.data
            : [];

        const checkoutConfirmed =
          hasConfirmedSubscription(pending, subscriptionResponse) ||
          hasConfirmedTransaction(pending, transactions);

        if (checkoutConfirmed) {
          await finalizeCheckoutSuccess({
            data: pending.paddleEventData,
          });
          return;
        }

        if (
          Date.now() - pending.startedAt >=
          CHECKOUT_VERIFICATION_TIMEOUT_MS
        ) {
          handleConfirmedCheckoutFailure(
            { data: pending.paddleEventData } as PaddleEventData,
            new Error('checkout_verification_timeout')
          );
          return;
        }

        shouldRetry = true;
      } catch (error) {
        if (
          Date.now() - pending.startedAt >=
          CHECKOUT_VERIFICATION_TIMEOUT_MS
        ) {
          handleConfirmedCheckoutFailure(
            { data: pending.paddleEventData } as PaddleEventData,
            error
          );
          return;
        }

        shouldRetry = true;
      } finally {
        verificationInFlightRef.current = false;

        if (shouldRetry) {
          verificationTimerRef.current = setTimeout(() => {
            void verifyPendingCheckout(pending);
          }, CHECKOUT_VERIFICATION_RETRY_MS);
        }
      }
    },
    [
      clearVerificationTimer,
      finalizeCheckoutSuccess,
      handleConfirmedCheckoutFailure,
      hasConfirmedSubscription,
      hasConfirmedTransaction,
    ]
  );

  useEffect(() => {
    if (!pendingCheckoutVerification) {
      return;
    }

    updatePaymentStatus('verifying');
    void verifyPendingCheckout(pendingCheckoutVerification);
  }, [pendingCheckoutVerification, updatePaymentStatus, verifyPendingCheckout]);

  const handleCheckoutCompleted = async (paddleEvent: any) => {
    const completedTransactionId = paddleEvent.data?.transaction_id;
    if (
      completedTransactionId &&
      !checkoutVisibilityStore.isVisible(completedTransactionId)
    ) {
      console.error(
        '[usePaddleEvents] CHECKOUT_COMPLETED received for a non-visible transaction',
        completedTransactionId
      );
    }

    try {
      const { planData, subscriptionData } =
        await checkoutService.complete(paddleEvent);
      await finalizeCheckoutSuccess(paddleEvent, {
        planData,
        subscriptionData,
      });
    } catch (error: any) {
      if (isVerificationCandidate(error)) {
        const pendingVerification: PendingCheckoutVerification = {
          startedAt: Date.now(),
          paddleEventData: paddleEvent.data,
          paywallType: resolveCheckoutPaywallType(paddleEvent),
          toolType: resolveCheckoutToolType(paddleEvent),
          transactionId: paddleEvent.data?.transaction_id,
          subscriptionId: paddleEvent.data?.subscription_id,
        };

        setPendingCheckoutVerification(pendingVerification);
        updatePaymentStatus('verifying');
        void verifyPendingCheckout(pendingVerification);
        return;
      }

      handleConfirmedCheckoutFailure(paddleEvent, error);
    }
  };

  const handlePaymentInitiated = async (paddleEvent: PaddleEventData) => {
    const initiatedTransactionId = (paddleEvent as any)?.data?.transaction_id;
    if (
      initiatedTransactionId &&
      !checkoutVisibilityStore.isVisible(initiatedTransactionId)
    ) {
      console.error(
        '[usePaddleEvents] CHECKOUT_PAYMENT_INITIATED received for a non-visible transaction',
        initiatedTransactionId
      );
    }

    updatePaymentStatus('processing');
    paymentInitiatedTimeRef.current = Date.now();

    if (!checkoutPurchaseClickLoggedRef.current) {
      const checkoutToolType = resolveCheckoutToolType(paddleEvent);

      checkoutPurchaseClickLoggedRef.current = true;
      logHubxEvent({
        eventName: EventsEnum.CHECKOUT_PURCHASE_BUTTON_CLICK,
        ...(checkoutToolType
          ? {
              source: formatAnalyticsSource(checkoutToolType),
            }
          : {}),
      });

      const purchaseItem = paddleEvent.data?.items?.[0] as any;
      const purchaseProductId =
        purchaseItem?.product?.id ||
        purchaseItem?.product_id ||
        purchaseItem?.price?.product_id ||
        purchaseItem?.price_id;
      const purchaseEventParams = {
        paywallType: resolveCheckoutPaywallType(paddleEvent),
        product_id: purchaseProductId,
        currency: paddleEvent.data?.currency_code,
        amount: paddleEvent.data?.totals?.total,
      };

      logHubxEvent({
        eventName: COMMON_EVENTS.PURCHASE_BUTTON_CLICK,
        event_params: purchaseEventParams,
      });

      if (typeof window !== 'undefined' && (window as any).dataLayer) {
        const analyticsUserData = userRef.current
          ? getUserData(userRef.current)
          : undefined;

        (window as any).dataLayer.push({
          event: 'purchase_button_click_v2',
          internal_use: false,
          checkout_provider: 'paddle',
          user_data: analyticsUserData,
          ...purchaseEventParams,
        });
      }
    }

    // Set a timeout to show paymentFailed after 15 seconds
    const timeoutId = setTimeout(() => {
      updatePaymentStatus('failed');
    }, 120000);

    return () => clearTimeout(timeoutId);
  };

  const handleCheckoutError = async (paddleEvent: PaddleEventData) => {
    const errorTransactionId = (paddleEvent as any)?.data?.transaction_id;

    if (!checkoutVisibilityStore.isVisible(errorTransactionId)) {
      const erroredPriceId = (paddleEvent as any)?.data?.items?.[0]?.price_id;
      if (erroredPriceId) {
        transactionPromiseStore.delete(erroredPriceId);
      }
      return;
    }

    clearPendingVerification();
    updatePaymentStatus('idle');
    sendGAEvent('event', 'checkout_error', {
      errorType: paddleEvent,
      timestamp: new Date().toISOString(),
    });

    const paywallType = resolveCheckoutPaywallType(paddleEvent);

    if (paddleEvent.name === CheckoutEventNames.CHECKOUT_PAYMENT_FAILED) {
      const amount = paymentInitiatedTimeRef.current
        ? Date.now() - paymentInitiatedTimeRef.current
        : undefined;
      const freePaywallType = freeTypeForUserAccessLevelRef.current;

      logHubxEvent({
        eventName: COMMON_EVENTS.PAYMENT_FAILED,
        event_params: {
          amount,
          info: 'fail',
          paywallType,
          source: 'purchase_fail',
        },
      });

      if (freePaywallType && freePaywallType !== paywallType) {
        logHubxEvent({
          eventName: COMMON_EVENTS.PAYMENT_FAILED,
          event_params: {
            amount,
            info: 'fail',
            paywallType: freePaywallType,
            source: 'purchase_fail',
          },
        });
      }
    } else {
      const purchaseFailSource =
        paddleEvent.name === CheckoutEventNames.CHECKOUT_ERROR
          ? 'checkout error'
          : 'checkout failed';

      logHubxEvent({
        eventName: COMMON_EVENTS.PURCHASE_FAIL,
        event_params: {
          source: purchaseFailSource,
        },
      });
    }

    dispatch(
      showSide({
        sideKey: 'paymentFailed',
      })
    );
  };

  const handleCheckoutLoaded = async (paddleEvent: PaddleEventData) => {
    const loadedTransactionId = (paddleEvent as any)?.data?.transaction_id;

    if (!checkoutVisibilityStore.isVisible(loadedTransactionId)) {
      if (loadedTransactionId) {
        checkoutVisibilityStore.parkEvent(loadedTransactionId, paddleEvent);
      }
      return;
    }

    checkoutPurchaseClickLoggedRef.current = false;
    updatePaymentStatus('checkout_ready');

    const pricingSnapshot = extractPricingSnapshot(paddleEvent);
    if (pricingSnapshot) {
      checkoutPricingStore.setPricing(pricingSnapshot);
    }
  };

  const handleCheckoutUpdated = async (paddleEvent: PaddleEventData) => {
    const updatedTransactionId = (paddleEvent as any)?.data?.transaction_id;

    if (!checkoutVisibilityStore.isVisible(updatedTransactionId)) {
      if (updatedTransactionId) {
        checkoutVisibilityStore.parkEvent(updatedTransactionId, paddleEvent);
      }
      return;
    }

    const pricingSnapshot = extractPricingSnapshot(paddleEvent);
    if (pricingSnapshot) {
      checkoutPricingStore.setPricing(pricingSnapshot);
    }
  };

  const handleCheckoutClosed = async (paddleEvent: PaddleEventData) => {
    const closedTransactionId = (paddleEvent as any)?.data?.transaction_id;

    if (!checkoutVisibilityStore.isVisible(closedTransactionId)) {
      return;
    }

    updatePaymentStatus('idle');
  };

  const eventHandler = async (paddleEvent: any) => {
    try {
      if (paddleEvent.type === 'checkout.ping.size') {
        if (typeof paddleEvent.height === 'number' && paddleEvent.height > 0) {
          checkoutRevealStore.setContentHeight(paddleEvent.height);
        }

        if (
          paddleEvent.height === 655 &&
          checkoutVisibilityStore.getVisibleTransactionId() != null
        ) {
          updatePaymentStatus('checkout_ready');
        }
      }
      switch (paddleEvent.name) {
        case CheckoutEventNames.CHECKOUT_LOADED:
          await handleCheckoutLoaded(paddleEvent);
          break;
        case CheckoutEventNames.CHECKOUT_UPDATED:
          await handleCheckoutUpdated(paddleEvent);
          break;
        case CheckoutEventNames.CHECKOUT_CLOSED:
          await handleCheckoutClosed(paddleEvent);
          break;
        case CheckoutEventNames.CHECKOUT_COMPLETED:
          await handleCheckoutCompleted(paddleEvent);
          break;
        case CheckoutEventNames.CHECKOUT_PAYMENT_INITIATED:
          await handlePaymentInitiated(paddleEvent);
          break;
        case CheckoutEventNames.CHECKOUT_FAILED:
        case CheckoutEventNames.CHECKOUT_ERROR:
        case CheckoutEventNames.CHECKOUT_PAYMENT_FAILED:
          await handleCheckoutError(paddleEvent);
          break;
        default:
      }
    } catch (error) {
      updatePaymentStatus('failed');
    }
  };

  useEffect(() => {
    return checkoutVisibilityStore.subscribe((_transactionId, events) => {
      events.forEach((event) => {
        void eventHandler(event);
      });
    });
  }, [eventHandler]);

  return {
    eventHandler,
  };
};

export default usePaddleEvents;
